import { AgendaItemsRoot, NoSessionWrapper } from './styles';
import {
  AgendaSpeaker,
  PublicSessionItem,
  PublicTrackItem,
} from '@hubs101/booking-api-skd-client/lib/types';

import AgendaItem from '@composites/AgendaItem';
import dayjs from 'dayjs';
import { getAgendaFilters, getEventDetails } from '@store/slices/eventSlice';
import { getImageSrc, groupAndSortByDateTimezoneIndependent } from '@utils/fn';
import { isIntersect } from './helpers';
import { useHookParams } from '@utils/hooks';
import { Fragment, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AgendaItemV2 from '@composites/AgendaItemV2';
import { Banner } from './banner';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

interface AgendaItemsProps {
  agendaDaysFormat?: string[];
  activeDay?: string;
  agendasSessions: PublicSessionItem[];
  agendasTracks: PublicTrackItem[];
  agendasIsLoading: boolean;
  selectedEventIds: string[];
  topLevelSelectedEventIds?: string[];
  eventIdMapping?: Record<string, string[]>;
  coLocatedEventsItems?: Array<{ key: string; value: string; color: string }>;
  minimalDetails?: boolean;
  showDescriptionOnMobile?: boolean;
  enableEventFilter?: boolean;
}

const AgendaItems = ({
  agendasSessions,
  agendasTracks,
  agendasIsLoading,
  selectedEventIds,
  topLevelSelectedEventIds,
  eventIdMapping,
  coLocatedEventsItems,
  agendaDaysFormat,
  activeDay,
  minimalDetails,
  enableEventFilter = false,
}: AgendaItemsProps) => {
  const { t } = useTranslation();
  const [day] = useHookParams(['day']);
  const filters = useSelector(getAgendaFilters);
  const sluggedEvent = useSelector(getEventDetails)?.[0];

  const getSessionEventId = (s: any): string | undefined => {
    const e = s?.event;
    if (!e) return undefined;
    if (typeof e === 'string') return e; // "uuid"
    if (typeof e === 'object') return e.id || e.event_id || e.eventId; // { id: "uuid" }
    return undefined;
  };

  const forcedLogoEventId = useMemo(() => {
    if (enableEventFilter && topLevelSelectedEventIds?.length === 1) {
      return topLevelSelectedEventIds[0];
    }
    return undefined;
  }, [enableEventFilter, topLevelSelectedEventIds]);

  const getDisplayLogoForSession = useCallback(
    (session: PublicSessionItem): string | undefined => {
      if (
        topLevelSelectedEventIds?.length !== 1 &&
        topLevelSelectedEventIds &&
        topLevelSelectedEventIds.length > 1 &&
        eventIdMapping
      ) {
        const ev = session?.event as any;
        const sid =
          typeof ev === 'string' ? ev : ev?.id || ev?.event_id || ev?.eventId;

        if (sid) {
          for (const topId of topLevelSelectedEventIds) {
            const owned = eventIdMapping[topId] ?? [];

            if (owned.includes(sid) || sid === topId) {
              return topId;
            }
          }
        }
      }

      return undefined;
    },
    [topLevelSelectedEventIds, eventIdMapping]
  );

  const filteredAgendaItems = useMemo(() => {
    let newFilteredAgenda =
      agendasSessions?.filter(
        e =>
          e?.start_date?.split('T')[0] ===
          dayjs(day, 'MM-DD-YYYY').format('YYYY-MM-DD')
      ) ?? [];

    if (enableEventFilter && selectedEventIds && selectedEventIds.length > 0) {
      newFilteredAgenda = newFilteredAgenda.filter(session => {
        const evId = getSessionEventId(session);
        return evId ? selectedEventIds.includes(evId) : true;
      });
    }

    Object.keys(filters)?.reduce((result: unknown[], element) => {
      if (element === 'tracks') {
        newFilteredAgenda = newFilteredAgenda?.filter(e => {
          const sessionTracks = e?.tracks?.map(
            track => agendasTracks?.find(e => e.id === track)?.name as string
          );
          return isIntersect(sessionTracks, filters?.tracks);
        });
      }
      if (element === 'session_type') {
        newFilteredAgenda = newFilteredAgenda?.filter(e =>
          isIntersect([e?.type], filters?.session_type)
        );
      }
      if (element === 'location') {
        newFilteredAgenda = newFilteredAgenda?.filter(e =>
          isIntersect([e?.location?.name], filters?.location)
        );
      }
      if (element === 'company_name') {
        newFilteredAgenda = newFilteredAgenda?.filter(e =>
          isIntersect(
            e.speakers?.map((s: AgendaSpeaker) => s?.company.name),
            filters?.company_name
          )
        );
      }
      return newFilteredAgenda;
    }, []);

    return Object.values(
      groupAndSortByDateTimezoneIndependent(newFilteredAgenda, 'start_date')
    )?.[0];
  }, [
    agendasSessions,
    agendasTracks,
    day,
    filters,
    selectedEventIds,
    enableEventFilter,
  ]);

  return (
    <AgendaItemsRoot>
      {agendasIsLoading ? (
        <AgendaItem isLoading />
      ) : (
        <>
          {filteredAgendaItems?.length > 0 ? (
            <>
              {filteredAgendaItems?.map((e, index) => {
                const perItemLogoEventId =
                  forcedLogoEventId ?? getDisplayLogoForSession(e);

                return (
                  <Fragment key={e.id || index}>
                    <AgendaItemV2
                      agendaDaysFormat={agendaDaysFormat}
                      activeDay={activeDay}
                      session={e}
                      agendasTracks={agendasTracks}
                      minimalDetails={minimalDetails}
                      displayLogoForEventId={perItemLogoEventId}
                    />

                    {((index + 1) % 5 === 0 ||
                      ((filteredAgendaItems?.length ?? 0) < 5 &&
                        index === filteredAgendaItems.length - 1)) && (
                      <Banner
                        bgImage={getImageSrc(
                          'https://media.we-conect.com/images/website/agenda_book_now_section.png',
                          sluggedEvent?.portfolio?.slug
                        )}
                      />
                    )}
                  </Fragment>
                );
              })}
            </>
          ) : (
            <NoSessionWrapper>{t('no-sessions-found')}</NoSessionWrapper>
          )}
        </>
      )}
    </AgendaItemsRoot>
  );
};

export default AgendaItems;
